import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useNbaAllTimeGreatsLeaderboard } from "../../hooks/useNbaAllTimeGreatsLeaderboard";
import { useGreatsGallery } from "../../hooks/useGreatsGallery";
import { usePlayerHeadshots } from "../../hooks/usePlayerHeadshots";
import { getNbaAllTimeGreatsStat } from "../../constants/nbaAllTimeGreats";
import {
  buildGreatsGalleryItems,
  filterLeaderboardRowsByName,
  getNbaGreatsQualifier,
  parseNbaAllTimeGreatsSearch,
  serializeNbaAllTimeGreatsSearch,
  splitGreatsRows,
} from "../../utils/nbaAllTimeGreatsPage";
import { getHeadshotUrl } from "../../utils/playerHeadshots";
import {
  buildNbaLeaderboardRows,
  getNbaLeaderboardStatFormat,
} from "../../utils/nbaLeaderboardRows";
import { QUERY_PAGE_SIZE_OPTIONS } from "../../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  formatGridStatValue,
  getResultsGridSx,
  sortedClassFor,
} from "../../utils/resultsGrid";
import PlayerNameCell from "../../components/PlayerNameCell";
import GreatsFeatured from "../../components/allTimeGreats/GreatsFeatured";
import GreatsGallery from "../../components/allTimeGreats/GreatsGallery";
import NbaGreatsStatRail from "../../components/allTimeGreats/NbaGreatsStatRail";

const NbaLeaderboards = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { stat } = parseNbaAllTimeGreatsSearch(searchParams);
  const statConfig = getNbaAllTimeGreatsStat(stat);
  const qualifier = getNbaGreatsQualifier(stat);

  const [searchTerm, setSearchTerm] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState([
    { field: "statValue", sort: "desc" },
  ]);

  const { results, totalResults, isLoading, error } =
    useNbaAllTimeGreatsLeaderboard({
      stat,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    });

  const sortedField = sortModel[0]?.field;
  const statFormat = getNbaLeaderboardStatFormat(stat);

  const writeSearch = (nextStat) => {
    setSearchParams(serializeNbaAllTimeGreatsSearch({ stat: nextStat }), {
      replace: true,
    });
    setPaginationModel((model) => ({ ...model, page: 0 }));
    setSearchTerm("");
    setSortModel([{ field: "statValue", sort: "desc" }]);
  };

  const rows = useMemo(
    () =>
      buildNbaLeaderboardRows(results, stat, {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }),
    [paginationModel.page, paginationModel.pageSize, results, stat]
  );

  const playerIds = useMemo(
    () => rows.map((row) => row.playerId).filter(Boolean),
    [rows]
  );
  const { headshots } = usePlayerHeadshots(playerIds, { sport: "nba" });

  const namedRows = useMemo(
    () => filterLeaderboardRowsByName(rows, searchTerm),
    [rows, searchTerm]
  );

  const visibleRows = useMemo(
    () =>
      namedRows.map((row) => ({
        ...row,
        displayValue: formatGridStatValue(row.statValue, statFormat),
        photoUrl: getHeadshotUrl(headshots, row.playerId),
      })),
    [headshots, namedRows, statFormat]
  );

  const { featured, list } = useMemo(
    () =>
      splitGreatsRows(visibleRows, {
        page: paginationModel.page,
        searchTerm,
      }),
    [paginationModel.page, searchTerm, visibleRows]
  );

  const galleryItems = useMemo(
    () => buildGreatsGalleryItems(featured, list),
    [featured, list]
  );
  const gallery = useGreatsGallery(galleryItems);

  const columns = useMemo(
    () => [
      {
        field: "rank",
        headerName: "#",
        width: 56,
        sortable: false,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "playerName",
        headerName: "Player",
        flex: 1.2,
        minWidth: 200,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            playerId={params.row.playerId}
            name={params.value}
            photoUrl={params.row.photoUrl}
            sport="nba"
            onPlayerClick={gallery.openAt}
          />
        ),
      },
      {
        field: "position",
        headerName: "Pos",
        width: 72,
        sortable: false,
      },
      {
        field: "statValue",
        headerName: statConfig.label,
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("statValue", sortedField),
        cellClassName: sortedClassFor("statValue", sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) =>
          params.row.displayValue ??
          formatGridStatValue(params.value, statFormat),
      },
    ],
    [gallery.openAt, sortedField, statConfig.label, statFormat]
  );

  return (
    <Container maxWidth="lg" sx={{ py: 1 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: "0.16em" }}
      >
        NBA all-time greats
      </Typography>
      <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
        {statConfig.label}
      </Typography>
      {qualifier && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {qualifier}
        </Typography>
      )}

      <Box sx={{ mt: 3 }}>
        <NbaGreatsStatRail stat={stat} onStatChange={writeSearch} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load this leaderboard.
        </Alert>
      )}

      <GreatsFeatured
        players={featured}
        loading={isLoading && paginationModel.page === 0 && !searchTerm}
        sport="nba"
        onSelect={gallery.openAt}
      />

      <TextField
        label="Search this page"
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Player name"
        sx={{ mb: 2, maxWidth: 320 }}
      />

      <Paper sx={{ overflow: "hidden" }}>
        <DataGrid
          rows={list}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          rowCount={totalResults}
          disableRowSelectionOnClick
          disableColumnMenu
          sortingMode="client"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onRowClick={(params) => gallery.openAt(params.row.playerId)}
          autoHeight
          rowHeight={52}
          localeText={{
            noRowsLabel: searchTerm.trim()
              ? "No players on this page match."
              : "No players",
          }}
          sx={{
            ...getResultsGridSx("auto"),
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
        />
      </Paper>
      <GreatsGallery
        open={gallery.open}
        item={gallery.item}
        index={gallery.index}
        count={gallery.count}
        statLabel={statConfig.label}
        sport="nba"
        onClose={gallery.close}
        onStep={gallery.step}
      />
    </Container>
  );
};

export default NbaLeaderboards;
